import './ActivityLog.scss';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { S_PickySelect } from '../../../../../../design/inputs/select/S_PickySelect';
import { S_TextInput } from '../../../../../../design/inputs/textInput/S_TextInput';
import { ColumnLayout } from '../../../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { RowLayout } from '../../../../../../modules/common/layouts/rowLayout/RowLayout';
import { GTable } from '../../../../../components/table/GTable';
import { IGColumnProps } from '../../../../../components/table/IGColumnProps';
import { toLocaleDateTimeString } from '../../../../../helpers/date.helpers';
import { IPagingInfoWithOrder } from '../../../../../models/paginationInfo.model';
import { ExportDataFilter } from '../../../../projects/projects.model';
import { allProjectActivities, IProjectActivity, ProjectActivity } from '../projectDetailsActivities.models';
import { AddAttributeActivity } from './activities/AddAttributeActivity';
import { AddAttributeAnswerActivity } from './activities/AddAttributeAnswerActivity';
import { AddAttributeScopeActivity } from './activities/AddAttributeScopeActivity';
import { AddDatasetActivity } from './activities/AddDatasetActivity';
import { AddQuestionActivity } from './activities/AddQuestionActivity';
import { AddQuestionAnswerActivity } from './activities/AddQuestionAnswerActivity';
import { AddToolActivity } from './activities/AddToolActivity';
import { AddUserActivity } from './activities/AddUserActivity';
import { ChangeImageAssignmentPolicyActivity } from './activities/ChangeImageAssignmentPolicyActivity';
import { DescriptionChangeActivity } from './activities/DescriptionChangeActivity';
import { DownloadExportActivity } from './activities/DownloadExportActivity';
import { DraftCreatedActivity } from './activities/DraftCreatedActivity';
import { ExportActivity } from './activities/ExportActivity';
import { ImportActivity } from './activities/ImportActivity';
import { ImportAnnotationsActivity } from './activities/ImportAnnotationsActivity';
import { RemoveAttributeActivity } from './activities/RemoveAttributeActivity';
import { RemoveAttributeAnswerActivity } from './activities/RemoveAttributeAnswerActivity';
import { RemoveAttributeScopeActivity } from './activities/RemoveAttributeScopeActivity';
import { RemoveDatasetActivity } from './activities/RemoveDatasetActivity';
import { RemoveQuestionActivity } from './activities/RemoveQuestionActivity';
import { RemoveQuestionAnswerActivity } from './activities/RemoveQuestionAnswerActivity';
import { RemoveToolActivity } from './activities/RemoveToolActivity';
import { RemoveUserActivity } from './activities/RemoveUserActivity';
import { RenameActivity } from './activities/RenameActivity';
import { UpdateAttributeActivity } from './activities/UpdateAttributeActivity';
import { UpdateAttributeAnswerActivity } from './activities/UpdateAttributeAnswerActivity';
import { UpdateQuestionActivity } from './activities/UpdateQuestionActivity';
import { UpdateQuestionAnswerActivity } from './activities/UpdateQuestionAnswerActivity';
import { UpdateToolActivity } from './activities/UpdateToolActivity';

interface IActivityLogProps {
  activities: IProjectActivity<never>[];
  isLoading: boolean;
  selectedActivities: ProjectActivity[];
  searchValue: string;
  paging: IPagingInfoWithOrder;
  onOrderingChange: (orderBy: string, orderType: string) => Promise<void>;
  onPaginationChange: (pageNumber: number, pageSize: number) => Promise<void>;
  onActorChange: (value: string) => Promise<void>;
  onActivitiesChange: (values: ProjectActivity[]) => Promise<void>;
  onExportDownload: (backupId: string, schemaVersion: string, selectedDataFilter?: ExportDataFilter) => Promise<void>;
}

export const ActivityLog = observer((props: IActivityLogProps): ReactElement => {
  const { t } = useTranslator();

  const renderCreatedDate = (a: IProjectActivity<never>): ReactElement => <>{toLocaleDateTimeString(a.createdDate)}</>;

  const activityRenderer = (data: IProjectActivity<never>): ReactElement => {
    switch (data.activity) {
      case ProjectActivity.DraftCreated:
        return <DraftCreatedActivity />;
      case ProjectActivity.Rename:
        return <RenameActivity {...data} />;
      case ProjectActivity.DescriptionChange:
        return <DescriptionChangeActivity {...data} />;
      case ProjectActivity.AddDataset:
        return <AddDatasetActivity {...data} />;
      case ProjectActivity.RemoveDataset:
        return <RemoveDatasetActivity {...data} />;
      case ProjectActivity.AddTool:
        return <AddToolActivity {...data} />;
      case ProjectActivity.UpdateTool:
        return <UpdateToolActivity {...data} />;
      case ProjectActivity.RemoveTool:
        return <RemoveToolActivity {...data} />;
      case ProjectActivity.AddQuestion:
        return <AddQuestionActivity {...data} />;
      case ProjectActivity.UpdateQuestion:
        return <UpdateQuestionActivity {...data} />;
      case ProjectActivity.RemoveQuestion:
        return <RemoveQuestionActivity {...data} />;
      case ProjectActivity.AddUser:
        return <AddUserActivity {...data} />;
      case ProjectActivity.RemoveUser:
        return <RemoveUserActivity {...data} />;
      case ProjectActivity.Publish:
        return <span>{t('activity_description_Publish')}.</span>;
      case ProjectActivity.Export:
        return <ExportActivity {...data} />;
      case ProjectActivity.DownloadExport:
        return <DownloadExportActivity {...data} />;
      case ProjectActivity.Import:
        return <ImportActivity {...data} />;
      case ProjectActivity.ImportAnnotations:
        return <ImportAnnotationsActivity {...data} />;
      case ProjectActivity.AddAttribute:
        return <AddAttributeActivity {...data} />;
      case ProjectActivity.UpdateAttribute:
        return <UpdateAttributeActivity {...data} />;
      case ProjectActivity.RemoveAttribute:
        return <RemoveAttributeActivity {...data} />;
      case ProjectActivity.AddAttributeAnswer:
        return <AddAttributeAnswerActivity {...data} />;
      case ProjectActivity.UpdateAttributeAnswer:
        return <UpdateAttributeAnswerActivity {...data} />;
      case ProjectActivity.RemoveAttributeAnswer:
        return <RemoveAttributeAnswerActivity {...data} />;
      case ProjectActivity.AddAttributeScope:
        return <AddAttributeScopeActivity {...data} />;
      case ProjectActivity.RemoveAttributeScope:
        return <RemoveAttributeScopeActivity {...data} />;
      case ProjectActivity.AddQuestionAnswer:
        return <AddQuestionAnswerActivity {...data} />;
      case ProjectActivity.UpdateQuestionAnswer:
        return <UpdateQuestionAnswerActivity {...data} />;
      case ProjectActivity.RemoveQuestionAnswer:
        return <RemoveQuestionAnswerActivity {...data} />;
      case ProjectActivity.ChangeImageAssignmentPolicy:
        return <ChangeImageAssignmentPolicyActivity {...data} />;
      default:
        throw 'Undefined action';
    }
  };

  const columns = useMemo<IGColumnProps<IProjectActivity<never>>[]>(
    () => [
      {
        sortBy: 'createdDate',
        headerName: t('date'),
        renderer: renderCreatedDate,
      },
      {
        sortBy: 'actor',
        headerName: t('actor'),
        renderer: (x): string => x.actor,
      },
      {
        sortBy: 'activity',
        headerName: t('activity'),
        renderer: activityRenderer,
      },
    ],
    [t, renderCreatedDate, activityRenderer]
  );

  const options = useMemo(() => allProjectActivities.map((o) => ({ label: t(`activity_type_${o}`), value: o })), [t]);

  return (
    <ColumnLayout size='sm'>
      <RowLayout
        size='md'
        className='activity-search'
      >
        <div className='actor'>
          <S_TextInput
            id='user-dropdown'
            name='select'
            value={props.searchValue}
            placeholder={t('actor')}
            onChange={(val) => {
              void props.onActorChange(val);
            }}
          />
        </div>
        <div className='activity-type'>
          <S_PickySelect
            options={options}
            value={props.selectedActivities}
            onChange={(_, v) => {
              void props.onActivitiesChange(v);
            }}
          />
        </div>
      </RowLayout>
      <GTable<IProjectActivity<never>>
        isLoading={props.isLoading}
        columns={columns}
        items={props.activities}
        noItemsPlaceholder={t('no_activity')}
        paginationProps={{
          pageNumber: props.paging.pageNumber,
          pageSize: props.paging.pageSize,
          totalCount: props.paging.totalCount,
          onChange: props.onPaginationChange,
        }}
        onSortChanged={props.onOrderingChange}
        sortingModel={[{ orderBy: props.paging.orderBy || '', orderType: props.paging.orderType || '' }]}
      />
    </ColumnLayout>
  );
});
